import { Modal } from '@sgcmpteltd/uikit-next';
import { useLatest } from '@sgcmpteltd/uikit-react';
import {
  setCache as setLocalStorageCache,
} from '@sgcmpteltd/uikit-react/utils/cache/local-storage';
import cx from 'clsx';
import { useContext, useEffect, useState } from 'react';

import { ACTION_STATUS_ERROR, ACTION_STATUS_SUCCESS } from '@/actions';
import {
  reportError,
} from '@/actions/misc/home/report-error/new-car-promotion/post';
import { postShareViaEmail } from '@/actions/misc/share/email/post';
import {
  getPromotionShortlistForm,
} from '@/actions/new-car/promotion-shortlist-form/get';
import {
  postPromotionShortlistForm,
} from '@/actions/new-car/promotion-shortlist-form/post';
import {
  convertToDealerTypeAbbr,
} from '@/apis/server/new-car/v1/promotion-shortlist-form';
import type {
  CamelCaseResponseData as PromotionShortlistFormData,
} from '@/apis/server/new-car/v1/promotion-shortlist-form/get';
import { UserContext } from '@/context/user';
import { joinSgcmMsSite } from '@/utils/url/join/public';

import {
  CACHE_KEY_NEWCAR_PROMOTION_DATA,
  CACHE_TTL_NEWCAR_PROMOTION_DATA,
  MODAL_STEP_CHECK_SHORTLIST,
  MODAL_STEP_CHECK_SHORTLIST_RESULT,
  MODAL_STEP_PROCESS_RESULT,
  MODAL_STEP_PROMOTION_DETAILS,
  MODAL_STEP_REPORT_ERROR,
  MODAL_STEP_SHARE_VIA_EMAIL,
  MODAL_TRANSITION_DURATION_MS_DEFAULT,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
  PROCESS_RESULT_MESSAGE_SHARE_VIA_EMAIL_FAIL,
  PROCESS_RESULT_MESSAGE_SHARE_VIA_EMAIL_SUCCESS,
  PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
  PROCESS_RESULT_TITLE_SHARE_VIA_EMAIL_FAIL,
  PROCESS_RESULT_TITLE_SHARE_VIA_EMAIL_SUCCESS,
} from './constants';
import { Step as CheckShortlistStep } from './step/check-shortlist';
import {
  Step as CheckShortlistResultStep,
} from './step/check-shortlist-result';
import { Step as ProcessResultStep } from './step/process-result';
import { Step as PromotionDetailsStep } from './step/promotion-details';
import { Step as ReportErrorStep } from './step/report-error';
import { Step as ShareViaEmailStep } from './step/share-via-email';
import styles from './styles.module.scss';
import type {
  Component,
  PostCheckShortlistResult,
  ProcessResult,
  Step,
} from './types';
import { getPromotionCarCode } from './utils';

const isBrowser = () => typeof window !== 'undefined';

export const PromotionDetailsModal: Component = ({
  promotion,
  isOpen,
  transitionDurationMs = MODAL_TRANSITION_DURATION_MS_DEFAULT,
  onClose,
  onExit,
  setOpen,
}) => {
  const [modalStep, setModalStep] = useState<Step>(
    MODAL_STEP_PROMOTION_DETAILS,
  );
  const [
    checkShortlistData,
    setCheckShortlistData,
  ] = useState<PromotionShortlistFormData | null>(null);

  const [
    postCheckShortlistResultData,
    setPostCheckShortlistResultData,
  ] = useState<PostCheckShortlistResult | null>(null);
  const [
    processResultData,
    setProcessResultData,
  ] = useState<ProcessResult | null>(null);
  const userContextData = useContext(UserContext);
  const isUserLogin = !!userContextData?.userId;
  const { ref: latestOnExitRef } = useLatest({
    value: onExit,
  });
  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');

  const [isAddToAlert, setIsAddToAlert] = useState(false);

  useEffect(() => {
    const fetchShortlistData = async () => {
      try {
        if (promotion) {
          const formData = new FormData();
          const dealerTypeAbbr = convertToDealerTypeAbbr(promotion.dealer.type);
          const promotionCarCode = getPromotionCarCode({
            carId: promotion.carId,
            dealerTypeAbbr,
          });

          formData.set('agent', `${promotion?.isCommercial ? 1 : 0}`);
          formData.set('carCode', promotionCarCode);
          formData.set('make', promotion?.make ?? '');
          formData.set('dealerTypeAbbr', dealerTypeAbbr);
          const actionResult = await getPromotionShortlistForm(formData);

          if (actionResult.status === ACTION_STATUS_SUCCESS) {
            // eslint-disable-next-line @stylistic/max-len
            const isChecked = actionResult.data.checkbox.some(item => item.checked);

            setIsAddToAlert(isChecked);
          }
        }
      } catch (error) {
        throw new Error('Fetching shortlist data failed');
      }
    };

    fetchShortlistData();
  }, [promotion]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { current: latestOnExit } = latestOnExitRef;

    if (!latestOnExit) {
      return undefined;
    }

    return () => {
      const { setTimeout, clearTimeout } = window;

      const timerId = setTimeout(() => {
        latestOnExit();
        clearTimeout(timerId);
      }, transitionDurationMs);
    };
  }, [
    isOpen,
    transitionDurationMs,
    latestOnExitRef,
  ]);

  if (!promotion) {
    return (
      <Modal
        className={styles.containerRoot}
        contentClassName={styles.containerModalContent}
        isOpen={isOpen}
        title=""
        transitionDurationMs={transitionDurationMs}
        isBackdropStatic
        onClose={onClose} />
    );
  }

  const {
    isCommercial,
    carId,
    dealer,
    description,
    endsOn,
    id: promotionId,
    image,
    link,
    make,
    models,
    postedOn,
    promoType,
    isAllModels,
  } = promotion;

  const promotionLink = joinSgcmMsSite(`new-cars/promotions?pid=${promotionId}`).toString();

  return (
    <Modal
      className={cx(
        styles.containerRoot,
        styles.isNormal,
        {
          [styles.isSubmitSuccess]: modalStep === MODAL_STEP_PROCESS_RESULT,
        },
        {
          [styles.isCheckShortList]: modalStep === MODAL_STEP_CHECK_SHORTLIST
          || modalStep === MODAL_STEP_CHECK_SHORTLIST_RESULT,
        },
      )}
      contentClassName={styles.containerModalContent}
      headerControlClassName={styles.headerControlClassName}
      isOpen={isOpen}
      title=""
      transitionDurationMs={transitionDurationMs}
      onClose={onClose}>
      {
        modalStep === MODAL_STEP_PROMOTION_DETAILS && (
          <div
            className={cx(
              styles.containerStep,
              styles.isPromotionDetails,
              styles.isNormal,
            )}>
            <PromotionDetailsStep
              dealer={dealer}
              description={description}
              endsOn={endsOn}
              id={promotionId}
              image={image}
              isAddToAlert={isAddToAlert}
              isAllModels={isAllModels}
              isUserLogin={isUserLogin}
              link={promotionLink}
              make={make}
              models={models}
              postedOn={postedOn}
              promoType={promoType}
              setOpen={setOpen}
              onAlertAdd={async () => {
                const dealerTypeAbbr = convertToDealerTypeAbbr(dealer.type);
                const promotionCarCode = getPromotionCarCode({
                  carId,
                  dealerTypeAbbr,
                });

                if (!isUserLogin) {
                  setLocalStorageCache({
                    key: CACHE_KEY_NEWCAR_PROMOTION_DATA,
                    value: {
                      adpi: dealerTypeAbbr,
                      isCommercial,
                      carcode: promotionCarCode,
                      carmake: make,
                      uid: '',
                    },
                    ttl: CACHE_TTL_NEWCAR_PROMOTION_DATA,
                  });

                  window.open(
                    joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
                    '_self',
                  );

                  return;
                }

                const formData = new FormData();

                formData.set('agent', `${isCommercial ? 1 : 0}`);
                formData.set('carCode', promotionCarCode);
                formData.set('make', make ?? '');
                formData.set('dealerTypeAbbr', dealerTypeAbbr);

                const actionResult = (
                  await getPromotionShortlistForm(formData)
                );

                if (actionResult.status !== ACTION_STATUS_SUCCESS) {
                  return;
                }

                setCheckShortlistData(actionResult.data);
                setModalStep(MODAL_STEP_CHECK_SHORTLIST);
              }}
              onErrorReport={() => {
                setModalStep(
                  MODAL_STEP_REPORT_ERROR,
                );
              }} />
          </div>
        )
      }
      {
        modalStep === MODAL_STEP_CHECK_SHORTLIST
        && !!checkShortlistData
        && (
          <div
            className={cx(
              styles.containerStep,
              styles.isCheckShortlist,
            )}>
            <div
              className={styles.containerStepContent}>
              <CheckShortlistStep
                alertPageLink={checkShortlistData.alertPageLink}
                checkList={checkShortlistData.checkbox}
                email={checkShortlistData.email}
                title={checkShortlistData.title}
                onSave={async ({
                  checkMap,
                }) => {
                  const formData = new FormData();
                  const shortlistCheckEntries = Object.entries(checkMap);
                  const dealerTypeAbbr = convertToDealerTypeAbbr(dealer.type);
                  const promotionCarCode = getPromotionCarCode({
                    carId,
                    dealerTypeAbbr,
                  });

                  for (const [checkKey, isChecked] of shortlistCheckEntries) {
                    formData.set(checkKey, `${+isChecked}`);
                  }

                  formData.set('agent', `${isCommercial ? 1 : 0}`);
                  formData.set('carCode', promotionCarCode);
                  formData.set('make', make ?? '');
                  formData.set(
                    'dealerTypeAbbr',
                    convertToDealerTypeAbbr(dealer.type),
                  );

                  const actionResult = await postPromotionShortlistForm(
                    formData,
                  );

                  switch (actionResult.status) {
                    case ACTION_STATUS_SUCCESS:
                      setPostCheckShortlistResultData({
                        isSuccess: true,
                        title: actionResult.data,
                      });

                      break;
                    case ACTION_STATUS_ERROR:
                    default:
                      setPostCheckShortlistResultData({
                        isSuccess: false,
                        title: 'Error occurred',
                      });

                      break;
                  }

                  setModalStep(
                    MODAL_STEP_CHECK_SHORTLIST_RESULT,
                  );
                }} />
            </div>
          </div>
        )
      }
      {
        modalStep === MODAL_STEP_CHECK_SHORTLIST_RESULT
        && !!checkShortlistData
        && !!postCheckShortlistResultData
        && (
          <div
            className={cx(
              styles.containerStep,
              styles.isCheckShortlist,
            )}>
            <div
              className={styles.containerStepContent}>
              <CheckShortlistResultStep
                alertPageLink={checkShortlistData.alertPageLink}
                isSuccess={postCheckShortlistResultData.isSuccess}
                title={postCheckShortlistResultData.title}
                onClose={onClose} />
            </div>
          </div>
        )
      }
      {
        modalStep === MODAL_STEP_REPORT_ERROR && (
          <div
            className={cx(
              styles.containerStep,
              styles.isReportError,
            )}>
            <div
              className={styles.containerStepContent}>
              <ReportErrorStep
                onSubmit={async ({
                  email: errorReportEmail,
                  description: errorReportDescription,
                  name: errorReportSenderName,
                }) => {
                  const formData = new FormData();

                  formData.set('email', errorReportEmail);
                  formData.set('description', errorReportDescription);
                  formData.set('link', link);
                  formData.set('name', errorReportSenderName);
                  const actionResult = await reportError(formData);

                  switch (actionResult.status) {
                    case ACTION_STATUS_SUCCESS:
                      setProcessResultData({
                        isSuccess: true,
                        title: PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
                        message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
                      });

                      break;
                    case ACTION_STATUS_ERROR:
                    default:
                      setProcessResultData({
                        isSuccess: false,
                        title: PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                        message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                      });
                      break;
                  }

                  setModalStep(
                    MODAL_STEP_PROCESS_RESULT,
                  );
                }} />
            </div>
          </div>
        )
      }
      {
        modalStep === MODAL_STEP_SHARE_VIA_EMAIL && (
          <div
            className={cx(
              styles.containerStep,
              styles.isShareViaEmail,
            )}>
            <div
              className={styles.containerStepContent}>
              <ShareViaEmailStep
                link={link}
                onSend={async ({
                  senderName,
                  recipientsJoinedEmailList,
                  emailMessage,
                }) => {
                  const formData = new FormData();

                  formData.set('email', recipientsJoinedEmailList);
                  formData.set('link', link);
                  formData.set('message', emailMessage);
                  formData.set('name', senderName);

                  const actionResult = await postShareViaEmail(formData);

                  switch (actionResult.status) {
                    case ACTION_STATUS_SUCCESS:
                      setProcessResultData({
                        isSuccess: true,
                        title: PROCESS_RESULT_TITLE_SHARE_VIA_EMAIL_SUCCESS,
                        message: PROCESS_RESULT_MESSAGE_SHARE_VIA_EMAIL_SUCCESS,
                      });

                      break;
                    case ACTION_STATUS_ERROR:
                    default:
                      setProcessResultData({
                        isSuccess: true,
                        title: PROCESS_RESULT_TITLE_SHARE_VIA_EMAIL_FAIL,
                        message: PROCESS_RESULT_MESSAGE_SHARE_VIA_EMAIL_FAIL,
                      });
                      break;
                  }

                  setModalStep(
                    MODAL_STEP_PROCESS_RESULT,
                  );
                }} />
            </div>
          </div>
        )
      }
      {
        modalStep === MODAL_STEP_PROCESS_RESULT
        && !!processResultData
        && (
          <div
            className={cx(
              styles.containerStep,
              styles.isProcessResult,
            )}>
            <div
              className={styles.containerStepContent}>
              <ProcessResultStep
                isSuccess={processResultData.isSuccess}
                message={processResultData.message}
                title={processResultData.title} />
            </div>
          </div>
        )
      }
    </Modal>
  );
};
